/** Identity / passport checklist and DocUpload routing (no FormContext import - checklist-safe). */

export interface IdentityProgressInput {
  docType?: string;
  /** Gate choice for passport vs other ID. */
  hasPassport?: "" | "yes" | "no";
  passportOcrSkipped?: string;
  docUpload?: string;
  identityUploads?: Partial<
    Record<"passport" | "nationalId" | "travelDoc" | "cedulaInscripcion" | "other", string>
  >;
  passportNumber?: string;
  docFullName?: string;
  dob?: { day?: string; month?: string; year?: string };
  placeOfBirth?: string;
  countryOfBirth?: string;
  sex?: string;
  nationalIdNumber?: string;
  travelDocumentNumber?: string;
  registrationDocumentNumber?: string;
  otherDocumentNumber?: string;
}

function strTrim(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

function passportUploadSlotActive(data: IdentityProgressInput): boolean {
  return data.docType === "passport" || data.hasPassport === "yes";
}

/** Passport PDF (or merged scan) filename recorded locally / draft. */
export function hasPassportPdfUploaded(data: IdentityProgressInput): boolean {
  if (strTrim(data.identityUploads?.passport)) return true;
  return passportUploadSlotActive(data) && !!strTrim(data.docUpload);
}

/** Uploaded filename for the active non-passport identity doc type. */
export function hasNonPassportIdentityFile(data: IdentityProgressInput): boolean {
  const dt = data.docType;
  if (!dt || dt === "passport") return false;
  const slot = data.identityUploads?.[dt];
  if (strTrim(slot)) return true;
  return !!strTrim(data.docUpload);
}

/** Matches passport Step 1 field gate shown on the identity DocUpload screen. */
export function arePassportDetailFieldsComplete(data: IdentityProgressInput): boolean {
  const d = data.dob ?? { day: "", month: "", year: "" };
  return (
    !!strTrim(data.passportNumber) &&
    !!strTrim(data.docFullName) &&
    !!strTrim(d.day) &&
    !!strTrim(d.month) &&
    !!strTrim(d.year) &&
    !!strTrim(data.placeOfBirth) &&
    !!strTrim(data.countryOfBirth) &&
    !!data.sex
  );
}

export function hasIdentityDocNumber(data: IdentityProgressInput): boolean {
  switch (data.docType) {
    case "passport":
      return !!strTrim(data.passportNumber);
    case "nationalId":
      return !!strTrim(data.nationalIdNumber);
    case "travelDoc":
      return !!strTrim(data.travelDocumentNumber);
    case "cedulaInscripcion":
      return !!strTrim(data.registrationDocumentNumber);
    case "other":
      return !!strTrim(data.otherDocumentNumber);
    default:
      return false;
  }
}

/**
 * Organizer + DocumentFlow “identity” checklist complete:
 * passport = PDF uploaded AND manual passport fields filled;
 * other ID types = doc number + upload filename.
 */
export function isIdentityCategoryComplete(data: IdentityProgressInput): boolean {
  if (data.hasPassport === "yes" && !data.docType) {
    return hasPassportPdfUploaded(data) && arePassportDetailFieldsComplete(data);
  }
  if (!data.docType) return false;
  if (data.docType === "passport") {
    return hasPassportPdfUploaded(data) && arePassportDetailFieldsComplete(data);
  }
  return hasIdentityDocNumber(data) && hasNonPassportIdentityFile(data);
}

/**
 * Passport path only (`hasPassport === "yes"`).
 * - `gate`: (1) PDF + all fields complete - show Passport | Other ID on entry; (2.1) no PDF and fields incomplete.
 * - `uploadLanding`: (2.2) no PDF but all required fields filled - scan/upload first.
 * - `detailForm`: (2.3) PDF present but fields still incomplete - details + Replace.
 */
export type PassportIdentityRouteSegment = "gate" | "uploadLanding" | "detailForm";

export function getPassportIdentityRouteSegment(data: IdentityProgressInput): PassportIdentityRouteSegment {
  if (data.hasPassport !== "yes") return "gate";
  const pdf = hasPassportPdfUploaded(data);
  const fields = arePassportDetailFieldsComplete(data);
  if (!pdf && !fields) return "gate";
  if (!pdf && fields) return "uploadLanding";
  if (pdf && !fields) return "detailForm";
  return "gate";
}

/** When true, open past the Passport | Other gate immediately (cases 2.2 and 2.3). */
export function shouldAutoSkipPassportIdentityGate(data: IdentityProgressInput): boolean {
  if (data.hasPassport !== "yes") return false;
  const pdf = hasPassportPdfUploaded(data);
  const fields = arePassportDetailFieldsComplete(data);
  return (!pdf && fields) || (pdf && !fields);
}

/** Passport path: scan/upload cards + interim OCR strip (hide once detail step takes over). */
export function shouldShowPassportUploadLanding(data: IdentityProgressInput): boolean {
  if (data.hasPassport !== "yes") return false;
  if (hasPassportPdfUploaded(data)) return false;
  /**
   * “Skip” on this screen must leave upload landing even when all manual fields are already
   * filled (2.2). Check before `arePassportDetailFieldsComplete`, which otherwise traps the UI.
   */
  if (data.passportOcrSkipped === "yes") return false;
  return true;
}

/** Passport path: Step 1 fields + saved-file row (not upload landing cards). */
export function shouldShowPassportIdentityDetailForm(data: IdentityProgressInput): boolean {
  if (data.hasPassport !== "yes") return false;
  if (shouldShowPassportUploadLanding(data)) return false;
  if (data.passportOcrSkipped === "yes") return true;
  /** Any saved passport file → details so users can edit or replace (never trap on partial data). */
  return hasPassportPdfUploaded(data);
}
